import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Column,
  Row,
  Heading,
  Page,
  Loader,
  Spinner,
  Spacer,
  Text,
  Empty,
  Tabs,
  ProgressBar,
} from '@oliasoft-open-source/react-ui-library';
import { useSocket, WEBSOCKET_EVENTS } from 'src/common/socket-contex/socket-context';
import {
  getCompareList,
  showCompareModalUpdated,
  addSimulationStatus,
  compareSelected,
  getCompareResult,
} from 'store/entities/compare-estimates/compare-estimates';
import { selectCompareEstimatesActiveEstimates } from 'store/entities/compare-estimates/selectors';
import { CompareList } from 'views/compare-estimates/compare.list';
import { CompareModal } from 'views/compare-estimates/compare.modal';
import translations from 'src/internationalisation/translation-map.json';
import { DeleteCompareModal } from 'views/compare-estimates/delete-compare.modal';
import { EditCompareModal } from 'views/compare-estimates/edit-compare.modal';
import { CompareEstimatesResultsTab } from 'views/compare-estimates/compare-estimates-results/compare-estimates-results-tab';
import { CompareEstimatesInputTab } from 'views/compare-estimates/compare-estimates-results/compare-estimates-input-tab';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { useCompareProgress } from 'src/common/progress/use-compare-progress';
import { CompareEstimatesDrawer } from './drawer/compare-estimates.drawer';
import { getSimulationStatus, getLoaderText } from './utils/utils';
import { convertToKebabCase } from 'src/common/utils/string-utils.ts';

const CompareEstimates = ({
  isFetching,
  isRecalculating,
  isGeneratingEstimates,
  showCompareModal,
  getCompareList,
  list,
  activeCompare,
  activeEstimates,
  visibleEstimates,
  showCompareModalUpdated,
  addSimulationStatus,
  getCompareResult,
  filteredEstimates,
  estimates,
  compareSelected,
  simulation,
}) => {
  const { t } = useTranslation();
  const { company } = useParams();
  const { socket } = useSocket();
  const [selectedId, setSelectedId] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [deleteCompareModalVisible, setDeleteCompareModalVisible] = useState(false);
  const [updateCompareModalVisible, setUpdateCompareModalVisible] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [estimateSelected, setEstimateSelected] = useState(null);

  const onClickDeleteCompare = (id) => {
    setSelectedId(id);
    setDeleteCompareModalVisible(true);
  };

  const onClickEditCompare = (compare) => {
    setEditItem(compare);
    setUpdateCompareModalVisible(true);
  };

  useEffect(() => {
    if (company) {
      getCompareList(company);
    }
    socket.on(WEBSOCKET_EVENTS.notification_message, (m) => addSimulationStatus({ notification: m.notification }));
    return () => {
      compareSelected(null);
    };
  }, []);

  useEffect(() => {
    setActiveTabIndex(0);
  }, [activeCompare?.compareId]);

  useEffect(() => {
    const statuses = Object.values(simulation);
    if (statuses.length && statuses.every((status) => status === 'Finished')) {
      getCompareResult();
    }
  }, [simulation]);
  const progress = useCompareProgress(
    getSimulationStatus(Object.values(simulation)),
    isRecalculating,
    isGeneratingEstimates,
  );
  const renderLoader = () => (
    <Loader text={getLoaderText('', isGeneratingEstimates)} theme="light" width="100%" height="90%">
      <ProgressBar percentage={progress} showProgressColors width="300px" />
    </Loader>
  );

  return (
    <>
      <Page left={0} padding={0}>
        {isFetching && (
          <Loader text={t(translations.fetching)} theme="light" cover>
            <Spinner dark />
          </Loader>
        )}
        <Row spacing={0} height="100%">
          <CompareList
            companyId={company}
            list={list}
            showCompareModalUpdated={showCompareModalUpdated}
            onClickDeleteCompare={onClickDeleteCompare}
            onClickEditCompare={onClickEditCompare}
            setEditItem={setEditItem}
            estimates={estimates}
            setEstimateSelected={setEstimateSelected}
            estimateSelected={estimateSelected}
          />
          {activeCompare ? (
            <>
              {activeTabIndex === 1 && (
                <CompareEstimatesDrawer estimates={activeEstimates} filteredEstimates={filteredEstimates} />
              )}
              <Column scroll padding>
                <Heading top testId="comparisons-title">
                  {activeCompare.name} <Spacer width={10} />
                  <Text faint>
                    {activeCompare?.designs?.length || visibleEstimates.length} {'Items'}
                  </Text>
                </Heading>
                <Tabs
                  testId="comparisons-tabs"
                  value={activeTabIndex}
                  onChange={(e) => setActiveTabIndex(e.target.value)}
                  margin={0}
                  options={[
                    {
                      value: 0,
                      label: t(translations.input),
                      testId: `comparisons-tabs-option-${convertToKebabCase(t(translations.input))}`,
                    },
                    {
                      value: 1,
                      label: t(translations.result),
                      testId: `comparisons-tabs-option-${convertToKebabCase(t(translations.result))}`,
                    },
                  ]}
                />
                {activeTabIndex === 0 && (
                  <CompareEstimatesInputTab
                    companyId={company}
                    compare={activeCompare}
                    visibleEstimates={visibleEstimates}
                    showCompareModalUpdated={showCompareModalUpdated}
                    filteredEstimates={filteredEstimates}
                    activeEstimates={activeEstimates}
                    setActiveTabIndex={(tabIdx) => setActiveTabIndex(tabIdx)}
                  />
                )}
                {activeTabIndex === 1 &&
                  (isRecalculating || isGeneratingEstimates ? (
                    renderLoader()
                  ) : (
                    <CompareEstimatesResultsTab
                      activeCompare={activeCompare}
                      visibleEstimates={visibleEstimates}
                      filteredEstimates={filteredEstimates}
                      estimateSelected={estimateSelected}
                    />
                  ))}
              </Column>
            </>
          ) : (
            <Empty height="100%" width="100%" text={t(translations.compare_noComparisonSelected)} />
          )}
        </Row>
      </Page>
      {deleteCompareModalVisible && (
        <DeleteCompareModal setModalVisible={setDeleteCompareModalVisible} selectedId={selectedId} />
      )}
      {updateCompareModalVisible && (
        <EditCompareModal compare={editItem} setModalVisible={setUpdateCompareModalVisible} />
      )}
      {showCompareModal && <CompareModal setModalVisible={showCompareModalUpdated} activeCompare={activeCompare} />}
    </>
  );
};

const mapStateToProps = (state) => {
  const {
    isFetching,
    isRecalculating,
    isGeneratingEstimates,
    list,
    estimates,
    showCompareModal,
    estimatesList,
    simulation,
  } = state.entities.compareEstimates;
  const activeCompare = list.find((item) => item.active);
  const updatedCompare = activeCompare
    ? { ...activeCompare, isPublic: String(activeCompare?.isPublic) }
    : activeCompare;
  const activeEstimates = selectCompareEstimatesActiveEstimates(state);
  const visibleEstimates = activeEstimates.filter((item) => item?.visible !== false);

  const filteredEstimates = estimatesList.filter((el) => visibleEstimates.some((f) => f.projectId === el.projectId));

  return {
    isFetching,
    isRecalculating,
    isGeneratingEstimates,
    list,
    estimates,
    activeCompare: updatedCompare,
    activeEstimates,
    showCompareModal,
    visibleEstimates,
    filteredEstimates,
    simulation,
  };
};

const mapDispatchToProps = {
  getCompareList,
  showCompareModalUpdated,
  addSimulationStatus,
  compareSelected,
  getCompareResult,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(CompareEstimates), {
  style: { position: 'absolute', top: '50px' },
});

export { Container as CompareEstimates };
